import Animated from 'react-native-reanimated'
import { useTranslation } from 'react-i18next'
import { router } from 'expo-router'
import { FlashList } from '@shopify/flash-list'
import { trpc } from 'api'
import cn from '@ui/utils/cn'
import { useRefreshByUser } from 'hooks/useRefreshByUser'
import useFlattenInfiniteData from 'hooks/useFlattenInfiniteData'
import Page from '@shell/Page'
import { CustomerCard } from '@user/customer/TileUser'
import { LoadingFooter } from '@ui/lists/LoadingFooter'
import EmptyResult from '@ui/components/EmptyResult'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import AnimatedEmpty from '@ui/animated/AnimatedEmpty'
import StaffCustomerLoginModal from '@user/StaffCustomerLoginModal'
import { useStaffLoginCustomer } from 'hooks/useStaffLoginCustomer'
import { useCustomer } from 'hooks/useSession'
import { Collapsible } from '@ui/animated/Collapsible'
import Header from '@ui/elements/Header'
import {
  useDealSelect,
  useDealTypeModalState,
} from '@order/hooks/useDealTypeModal'
import { useActiveDeal } from '@order/hooks/useDeal'

export default function StaffQuickLogin() {
  const { t } = useTranslation(['staff'])
  const text = 'staff:quickLogin'
  const { selectDealById } = useDealSelect()
  const activeDeal = useActiveDeal()
  // const { removeCustomer } = useQuickLogin()

  const {
    data,
    refetch,
    hasNextPage,
    isRefetching,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isInitialLoading,
    isFetching,
    isFetched,
  } = trpc.customer.quickLogin.list.useInfiniteQuery(
    {},
    {
      getNextPageParam: (lastPage) => {
        return lastPage.meta.nextCursor
      },
      placeholderData: (v) => v,
      refetchOnWindowFocus: false,
    }
  )

  const isLoadings =
    (isFetching || isInitialLoading || isLoading) && !isFetchingNextPage

  const showLoading = isLoadings
  const showEmpty = !isLoadings && isFetched
  const showItems = isFetched && !isLoadings

  const styleLoading = useAnimatedOpacity(showLoading)
  const styleEmpty = useAnimatedOpacity(showEmpty)
  const styleItems = useAnimatedOpacity(showItems)

  const flatData = useFlattenInfiniteData(data)
  const { isRefetchingByUser, refetchByUser } = useRefreshByUser(
    refetch,
    isRefetching
  )
  const verify = useStaffLoginCustomer()
  const sessionCustomer = useCustomer()
  const closeDealTypeModal = useDealTypeModalState((s) => s.close)

  return (
    <>
      <Page stage="staff" list>
        <FlashList
          ListHeaderComponent={
            <>
              <Header
                list
                title={t(`${text}.header.title`)}
                desc={t(`${text}.header.desc`)}
              />
              <Collapsible open={showLoading} fullWidth>
                <LoadingFooter style={styleLoading} />
              </Collapsible>
            </>
          }
          ListFooterComponent={isFetchingNextPage ? LoadingFooter : null}
          data={flatData}
          numColumns={1}
          onEndReachedThreshold={0.25}
          onRefresh={refetchByUser}
          refreshing={isRefetchingByUser}
          progressViewOffset={100}
          contentContainerClassName="pb-8"
          onEndReached={hasNextPage ? fetchNextPage : undefined}
          renderItem={({ item, index }) => (
            <Animated.View
              style={styleItems}
              className={cn('px-8 pt-8', index === 0 && 'pt-0')}
            >
              <CustomerCard
                data={item.customer_data}
                onPress={() => {
                  if (sessionCustomer?.id === item.customer_data.id) {
                    router.navigate('/customer/home')
                    return
                  }
                  const startLogin = () =>
                    verify.request(item.customer_data, 'staff')
                  if (item.deal_id && !activeDeal) {
                    selectDealById(item.deal_id, startLogin)
                    return
                  }
                  startLogin()
                }}
              />
            </Animated.View>
          )}
          ListEmptyComponent={
            <AnimatedEmpty style={styleEmpty} className="pt-0">
              <EmptyResult
                title={t(`${text}.empty.result.title`)}
                desc={t(`${text}.empty.result.desc`)}
                button={{
                  variant: 'filled',
                  label: t(`ui:allCustomers`),
                  onPress: () => router.navigate('/staff/customers'),
                }}
              />
            </AnimatedEmpty>
          }
        />
        <StaffCustomerLoginModal
          verify={verify}
          auto
          onSuccess={() => {
            closeDealTypeModal()
            verify.cancel()
          }}
        />
      </Page>
    </>
  )
}
